import React, { useState, useRef, useEffect } from 'react';
import '../styles/VoiceRecorder.css';

const VoiceRecorder = ({ onVoiceMessage, disabled = false }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [audioChunks, setAudioChunks] = useState([]);
  const [stream, setStream] = useState(null);
  const [error, setError] = useState(null);
  
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);

  // Cleanup function
  const cleanup = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    if (mediaRecorder) {
      mediaRecorder.stop();
      setMediaRecorder(null);
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setAudioChunks([]);
    setRecordingTime(0);
    setIsRecording(false);
    setError(null);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, []);

  const startRecording = async () => {
    try {
      setError(null);
      const mediaStream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100
        } 
      });
      
      setStream(mediaStream);
      
      const recorder = new MediaRecorder(mediaStream, {
        mimeType: 'audio/webm;codecs=opus'
      });
      
      const chunks = [];
      
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };
      
      recorder.onstop = () => {
        const audioBlob = new Blob(chunks, { type: 'audio/webm' });
        const audioUrl = URL.createObjectURL(audioBlob);
        
        // Create a file object
        const audioFile = new File([audioBlob], `voice-message-${Date.now()}.webm`, {
          type: 'audio/webm'
        });
        
        onVoiceMessage(audioFile, audioUrl);
        cleanup();
      };
      
      recorder.onerror = (event) => {
        console.error('MediaRecorder error:', event);
        setError('Recording failed. Please try again.');
        cleanup();
      };
      
      setMediaRecorder(recorder);
      setAudioChunks(chunks);
      
      // Start recording
      recorder.start(100); // Collect data every 100ms
      setIsRecording(true);
      setShowModal(true);
      startTimeRef.current = Date.now();
      
      // Start timer
      timerRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
        setRecordingTime(elapsed);
        
        // Auto-stop after 5 minutes
        if (elapsed >= 300) {
          stopRecording();
        }
      }, 1000);
      
    } catch (err) {
      console.error('Error starting recording:', err);
      if (err.name === 'NotAllowedError') {
        setError('Microphone access denied. Please allow microphone permissions.');
      } else if (err.name === 'NotFoundError') {
        setError('No microphone found. Please connect a microphone.');
      } else {
        setError('Failed to start recording. Please try again.');
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && mediaRecorder.state === 'recording') {
      mediaRecorder.stop();
    } else {
      cleanup();
    }
  };

  const cancelRecording = () => {
    if (mediaRecorder && mediaRecorder.state === 'recording') {
      mediaRecorder.onstop = null; // prevent sending callback
      mediaRecorder.stop();
    }
    cleanup();
    setShowModal(false);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleButtonClick = () => {
    if (disabled) return;
    
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  // Check if browser supports MediaRecorder
  const isSupported = () => {
    return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia && window.MediaRecorder);
  };

  if (!isSupported()) {
    return (
      <div className="voice-recorder">
        <button
          className="voice-record-btn disabled"
          disabled={true}
          title="Voice recording not supported in this browser"
        >
          <span className="voice-record-mic-icon" aria-hidden="true">🎤</span>
        </button>
      </div>
    );
  }

  return (
    <div className="voice-recorder">
      {error && (
        <div className="voice-error">
          {error}
        </div>
      )}
      
      <button
        className={`voice-record-btn ${isRecording ? 'recording' : ''} ${disabled ? 'disabled' : ''}`}
        onClick={handleButtonClick}
        disabled={disabled}
        title={isRecording ? 'Stop recording' : 'Start voice recording'}
      >
        {isRecording ? (
          <>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="6" y="6" width="12" height="12" rx="2"/>
            </svg>
            <span className="recording-time">{formatTime(recordingTime)}</span>
          </>
        ) : (
          <span className="voice-record-mic-icon" aria-hidden="true">🎤</span>
        )}
      </button>
      
      {isRecording && (
        <div className="recording-indicator">
          <div className="pulse-dot"></div>
          <span>Recording...</span>
        </div>
      )}

      {showModal && (
        <div className="voice-modal-overlay" onClick={cancelRecording}>
          <div className="voice-modal" onClick={(e) => e.stopPropagation()}>
            <div className="voice-modal-header">Voice message</div>
            <div className="voice-modal-body">
              <div className="voice-modal-status">
                <div className="pulse-dot"></div>
                <span>Recording... {formatTime(recordingTime)}</span>
              </div>
            </div>
            <div className="voice-modal-actions">
              <button className="voice-cancel-btn" onClick={cancelRecording}>Cancel</button>
              <button className="voice-stop-btn" onClick={() => { stopRecording(); setShowModal(false); }}>Stop & Send</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VoiceRecorder; 